import ReactMarkdown from "react-markdown";
import { Gauge, RefreshCw } from "lucide-react";
import { useTranslation } from "react-i18next";
import CardImage from "../tool_components/CardImage";
import { useMemo, useState } from "react";
import TextLevelerCardPDF from "./TextLevelerCardPdf";
import PdfDownloadButton from "../pdf/PdfDownloadButton";
import useImagePdf from "../pdf/useImagePdf";
import remarkGfm from "remark-gfm";
import { Button } from "@core/index";
import PdfCustomizationModal from "@features/common/components/pdf-customization-modal";
import { useAppSelector } from "@packages/store/hooks";

const TextLevelerCard = ({
  content,
  level,
  imageUrl,
  videoUrl,
  createdAt,
  author,
  onCreateAgain,
}: {
  content: string;
  level: string;
  imageUrl?: string;
  videoUrl?: string;
  createdAt: string;
  onCreateAgain: () => void;
  author: string;
}) => {
  const { t } = useTranslation();
  const finalImageUrl = useImagePdf(imageUrl);

  const levels = useMemo(
    () => [
      { value: 1, label: t("textLeveler.levels.elementary") },
      { value: 3, label: t("textLeveler.levels.intermediate") },
      { value: 5, label: t("textLeveler.levels.upperIntermediate") },
      { value: 7, label: t("textLeveler.levels.advanced") },
      { value: 9, label: t("textLeveler.levels.proficient") },
    ],
    [t],
  );
  const [showPdfCustomizationModal, setShowPdfCustomizationModal] =
    useState(false);
  const customizationSettings = useAppSelector(
    (state) => state.user.teacher?.preferences?.pdfOptions,
  );
  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-xl bg-gradient-to-r from-blue-600 to-sky-600 hover:from-blue-700 hover:to-sky-700 p-px">
      <div className="p-6 rounded-[10px] bg-white dark:bg-gray-800">
        {imageUrl && (
          <CardImage
            createdAt={createdAt}
            imageUrl={imageUrl}
            videoUrl={videoUrl}
            isLoading={false}
          />
        )}
        <div className="flex items-center gap-4 mb-6 mt-2">
          <div className="p-3 rounded-xl bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-400">
            <Gauge size={24} />
          </div>
          <div className="flex-1">
            <div
              className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-sky-500 dark:from-blue-400 dark:to-sky-300"
              data-testid="text-leveler-title"
            >
              {t("textLeveler.details")}
            </div>
            <div className="text-lg text-blue-600 dark:text-blue-400">
              {t("textLeveler.level")}:{" "}
              {levels.find((l) => l.value === Number(level))?.label}
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-2">
            {onCreateAgain && (
              <Button
                onClick={onCreateAgain}
                variant="solid"
                size="md"
                textColor="text-white dark:text-white"
                bgColor="bg-gradient-to-r from-blue-500 to-sky-500 hover:from-blue-600 hover:to-sky-600"
                rounded="xl"
              >
                <RefreshCw size={18} />
                <span>{t("common.createAnother")}</span>
              </Button>
            )}
            <PdfDownloadButton
              pdfTitle={content.slice(0, 20)}
              openCustomizationModal={() => setShowPdfCustomizationModal(true)}
              document={
                <TextLevelerCardPDF
                  author={author || ""}
                  createdAt={createdAt || ""}
                  content={content}
                  level={
                    levels.find((l) => l.value === Number(level))?.label || ""
                  }
                  imageUrl={finalImageUrl}
                  customizationSettings={customizationSettings}
                />
              }
              btnTheme="bg-gradient-to-r from-blue-500 to-sky-500"
            />
          </div>
        </div>

        <div
          className="prose dark:prose-invert max-w-none bg-gray-50 dark:bg-gray-800 rounded-xl 
            prose-table:border-collapse prose-td:border prose-td:border-gray-300 dark:prose-td:border-gray-600 
            prose-td:p-2 prose-th:border prose-th:border-gray-300 dark:prose-th:border-gray-600 prose-th:p-2
            prose-table:w-full prose-table:my-4"
          data-testid="text-leveler-content"
        >
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            className="prose dark:prose-invert max-w-none bg-gray-50 dark:bg-gray-800 rounded-xl 
              prose-table:border-collapse prose-td:border prose-td:border-gray-300 dark:prose-td:border-gray-600 
              prose-td:p-2 prose-th:border prose-th:border-gray-300 dark:prose-th:border-gray-600 prose-th:p-2
              prose-table:w-full prose-table:my-4 prose-pre:whitespace-pre-wrap prose-pre:break-words prose-pre:overflow-auto prose-pre:max-w-full"
          >
            {content}
          </ReactMarkdown>
        </div>
      </div>
      {/* PDF Customization Modal */}
      <PdfCustomizationModal
        show={showPdfCustomizationModal}
        onClose={() => setShowPdfCustomizationModal(false)}
        hideSchoolLogoOptions
        documentComponent={
          <TextLevelerCardPDF
            author={author || ""}
            createdAt={createdAt || ""}
            content={content}
            level={levels.find((l) => l.value === Number(level))?.label || ""}
            imageUrl={finalImageUrl}
            customizationSettings={customizationSettings}
          />
        }
      />
    </div>
  );
};

export default TextLevelerCard;
